'use client';

import { useEffect, useRef, type RefObject } from 'react';
import { RING_BUY_GTM_EVENTS } from './ringBuyEvents';
import { trackEvent } from 'components/shared/UserAnalytics/funnelEvents';

export type RingBuySection = 'size' | 'trade-in' | 'max' | 'order-summary';

type Target = {
  section: RingBuySection;
  ref: RefObject<HTMLDivElement | null>;
};

/** `ringBuySectionViewed` once per section per page view, when 30% of the
 *  section is on screen. `rescanKey` re-attaches observers as sections mount. */
export function useRingBuySectionImpressions(
  targets: Target[],
  rescanKey: string,
) {
  const seen = useRef(new Set<RingBuySection>());

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const section = (entry.target as HTMLElement).dataset
            .impressionSection as RingBuySection | undefined;
          if (!section || seen.current.has(section)) continue;
          seen.current.add(section);
          trackEvent(RING_BUY_GTM_EVENTS.SECTION_VIEWED, { section });
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.3 },
    );
    for (const { section, ref } of targets) {
      const el = ref.current;
      if (!el || seen.current.has(section)) continue;
      el.dataset.impressionSection = section;
      observer.observe(el);
    }
    return () => observer.disconnect();
    // targets is rebuilt each render; rescanKey is the intended trigger.
  }, [rescanKey]);
}
